import { useState, useRef } from 'react';
import AppHeader from '@/components/AppHeader';
import { useNavigate } from 'react-router-dom';
import { Team, Player } from '@/types';
import {
  createTeam,
  updateTeam,
  deleteTeam,
  createPlayer,
  updatePlayer,
  deletePlayer,
  uploadLogo,
} from '@/lib/supabase';

interface AdminPanelProps {
  teams: Team[];
  onDataChanged: () => Promise<void>;
  onSignOut: () => void;
}

type TeamFormMode = { mode: 'add' } | { mode: 'edit'; team: Team };
type PlayerFormMode = { mode: 'add'; teamId: string } | { mode: 'edit'; player: Player; teamId: string };
type DeleteTarget =
  | { type: 'team'; id: string; name: string }
  | { type: 'player'; id: string; name: string };

const ACCEPTED_TYPES = 'image/png,image/jpeg,image/jpg,image/webp';
const MAX_SIZE_BYTES = 5 * 1024 * 1024;

export default function AdminPanel({ teams, onDataChanged, onSignOut }: AdminPanelProps) {
  const navigate = useNavigate();

  const [expandedTeamId, setExpandedTeamId] = useState<string | null>(null);
  const [teamForm, setTeamForm] = useState<TeamFormMode | null>(null);
  const [playerForm, setPlayerForm] = useState<PlayerFormMode | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  // Team form fields
  const [teamName, setTeamName] = useState('');
  const [teamShortName, setTeamShortName] = useState('');
  const [teamColor, setTeamColor] = useState('#2563EB');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [existingLogoUrl, setExistingLogoUrl] = useState<string | null>(null);
  const [removeExistingLogo, setRemoveExistingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Player form fields
  const [playerName, setPlayerName] = useState('');
  const [playerNumber, setPlayerNumber] = useState<number | ''>('');

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const openAddTeam = () => {
    setTeamName('');
    setTeamShortName('');
    setTeamColor('#2563EB');
    setLogoFile(null);
    setLogoPreview(null);
    setExistingLogoUrl(null);
    setRemoveExistingLogo(false);
    setFormError(null);
    setTeamForm({ mode: 'add' });
  };

  const openEditTeam = (team: Team) => {
    setTeamName(team.name);
    setTeamShortName(team.shortName);
    setTeamColor(team.color);
    setLogoFile(null);
    setLogoPreview(null);
    setExistingLogoUrl(team.logoUrl ?? null);
    setRemoveExistingLogo(false);
    setFormError(null);
    setTeamForm({ mode: 'edit', team });
  };

  const openAddPlayer = (teamId: string) => {
    setPlayerName('');
    setPlayerNumber('');
    setFormError(null);
    setPlayerForm({ mode: 'add', teamId });
  };

  const openEditPlayer = (player: Player) => {
    setPlayerName(player.name);
    setPlayerNumber(player.number);
    setFormError(null);
    setPlayerForm({ mode: 'edit', player, teamId: player.teamId });
  };

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_SIZE_BYTES) {
      setFormError('Logo file must be 5 MB or smaller.');
      e.target.value = '';
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setRemoveExistingLogo(false);
    setFormError(null);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setRemoveExistingLogo(true);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleSaveTeam = async () => {
    const name = teamName.trim();
    const shortName = teamShortName.trim();
    if (!name) { setFormError('Team name is required.'); return; }
    if (!shortName) { setFormError('Short name is required.'); return; }
    if (shortName.length > 6) { setFormError('Short name must be 6 characters or less.'); return; }

    setSaving(true);
    setFormError(null);
    try {
      let logoUrl: string | null = removeExistingLogo ? null : (existingLogoUrl ?? null);

      if (logoFile) {
        const slug = shortName.toLowerCase().replace(/[^a-z0-9]/g, '-');
        logoUrl = await uploadLogo('team-logos', logoFile, `team-${slug}-${Date.now()}`);
      }

      if (teamForm?.mode === 'add') {
        await createTeam({ name, shortName, color: teamColor, logoUrl });
      } else if (teamForm?.mode === 'edit') {
        await updateTeam(teamForm.team.id, { name, shortName, color: teamColor, logoUrl });
      }
      await onDataChanged();
      setTeamForm(null);
    } catch {
      setFormError('Failed to save team. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePlayer = async () => {
    const name = playerName.trim();
    const number = typeof playerNumber === 'number' ? playerNumber : parseInt(String(playerNumber));
    if (!name) { setFormError('Player name is required.'); return; }
    if (!number || number < 1 || number > 99) { setFormError('Player number must be between 1 and 99.'); return; }

    setSaving(true);
    setFormError(null);
    try {
      if (playerForm?.mode === 'add') {
        await createPlayer({ teamId: playerForm.teamId, name, number });
      } else if (playerForm?.mode === 'edit') {
        await updatePlayer(playerForm.player.id, { name, number });
      }
      await onDataChanged();
      setPlayerForm(null);
    } catch {
      setFormError('Failed to save player. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.type === 'team') {
        await deleteTeam(deleteTarget.id);
        if (expandedTeamId === deleteTarget.id) setExpandedTeamId(null);
      } else {
        await deletePlayer(deleteTarget.id);
      }
      await onDataChanged();
      setDeleteTarget(null);
    } catch {
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const toggleExpand = (teamId: string) => {
    setExpandedTeamId(prev => prev === teamId ? null : teamId);
  };

  const currentLogoSrc = logoPreview ?? (removeExistingLogo ? null : existingLogoUrl);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader
        onSignOut={onSignOut}
        title="Team Management"
        navIcon="back"
        onNavigate={() => navigate('/admin')}
      />

      <div className="flex-1 overflow-y-auto px-4 py-4 safe-area-bottom space-y-3 max-w-md mx-auto w-full">

        {teams.length === 0 && (
          <div className="card text-center py-10">
            <p className="text-text-muted text-sm">No teams configured.</p>
            <p className="text-text-muted text-xs mt-1">Add a team to get started.</p>
          </div>
        )}

        {teams.map(team => (
          <div key={team.id} className="bg-surface border border-border rounded-lg overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3">
              {team.logoUrl ? (
                <img
                  src={team.logoUrl}
                  alt={team.shortName}
                  className="w-8 h-8 rounded object-contain flex-shrink-0"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <div className="w-4 h-4 rounded-full flex-shrink-0 border border-white/20" style={{ backgroundColor: team.color }} />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-text-primary text-sm leading-tight truncate">{team.name}</p>
                <p className="text-text-muted text-xs">{team.shortName} · {team.players.length} players</p>
              </div>
              <button onClick={() => openEditTeam(team)} className="text-xs text-text-secondary font-semibold px-2 py-1 rounded border border-border hover:border-text-secondary transition-colors">EDIT</button>
              <button onClick={() => setDeleteTarget({ type: 'team', id: team.id, name: team.name })} className="text-xs text-error font-semibold px-2 py-1 rounded border border-error/40 hover:border-error transition-colors">DEL</button>
              <button
                onClick={() => toggleExpand(team.id)}
                className="text-text-muted text-lg font-bold w-6 text-center transition-transform duration-200"
                style={{ transform: expandedTeamId === team.id ? 'rotate(45deg)' : 'rotate(0deg)' }}
              >+</button>
            </div>

            {expandedTeamId === team.id && (
              <div className="border-t border-border">
                {team.players.length === 0 && (
                  <p className="text-text-muted text-xs text-center py-3 px-4">No players. Add one below.</p>
                )}
                {team.players.slice().sort((a, b) => a.number - b.number).map((player, idx) => (
                  <div key={player.id} className={`flex items-center gap-3 px-4 py-2.5 ${idx !== 0 ? 'border-t border-border' : ''}`}>
                    <span className="text-xs font-bold w-6 text-center rounded" style={{ color: team.color }}>#{player.number}</span>
                    <span className="flex-1 text-sm text-text-primary">{player.name}</span>
                    <button onClick={() => openEditPlayer(player)} className="text-xs text-text-secondary font-semibold px-2 py-1 rounded border border-border hover:border-text-secondary transition-colors">EDIT</button>
                    <button onClick={() => setDeleteTarget({ type: 'player', id: player.id, name: player.name })} className="text-xs text-error font-semibold px-2 py-1 rounded border border-error/40 hover:border-error transition-colors">DEL</button>
                  </div>
                ))}
                <div className="px-4 py-2.5 border-t border-border">
                  <button onClick={() => openAddPlayer(team.id)} className="w-full py-2 text-xs font-bold text-serving border border-serving/40 hover:border-serving rounded-lg transition-colors">
                    + ADD PLAYER
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        <button onClick={openAddTeam} className="w-full py-4 rounded-lg font-bold text-sm bg-surface-raised border border-border hover:border-text-secondary text-text-primary transition-colors">
          + ADD TEAM
        </button>
      </div>

      {/* Team Form Modal */}
      {teamForm && (
        <div className="fixed inset-0 bg-black/80 z-40 flex items-end">
          <div className="w-full max-w-md mx-auto bg-surface border-t border-border rounded-t-2xl p-4 pb-safe animate-slide-up max-h-[90vh] overflow-y-auto">
            <div className="w-12 h-1 bg-border rounded-full mx-auto mb-4" />
            <h2 className="text-lg font-bold text-text-primary text-center mb-4">
              {teamForm.mode === 'add' ? 'ADD TEAM' : 'EDIT TEAM'}
            </h2>

            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs text-text-muted uppercase mb-1">Team Name</label>
                <input type="text" value={teamName} onChange={e => setTeamName(e.target.value)} className="input-field" placeholder="e.g. Walnut Warriors" maxLength={50} />
              </div>
              <div>
                <label className="block text-xs text-text-muted uppercase mb-1">Short Name (max 6 chars)</label>
                <input type="text" value={teamShortName} onChange={e => setTeamShortName(e.target.value.toUpperCase())} className="input-field" placeholder="e.g. WW" maxLength={6} />
              </div>
              <div>
                <label className="block text-xs text-text-muted uppercase mb-1">Team Color</label>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg border border-border flex-shrink-0" style={{ backgroundColor: teamColor }} />
                  <input type="color" value={teamColor} onChange={e => setTeamColor(e.target.value)} className="flex-1 h-12 rounded-lg border border-border cursor-pointer bg-surface-raised" style={{ padding: '2px' }} />
                </div>
              </div>

              {/* Logo Upload */}
              <div>
                <label className="block text-xs text-text-muted uppercase mb-2">Team Logo</label>
                <div className="flex items-center gap-3">
                  {currentLogoSrc ? (
                    <div className="relative flex-shrink-0">
                      <img src={currentLogoSrc} alt="Logo preview" className="w-16 h-16 rounded-lg object-contain border border-border bg-surface-raised" />
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-error rounded-full flex items-center justify-center text-white text-xs font-bold leading-none"
                        aria-label="Remove logo"
                      >×</button>
                    </div>
                  ) : (
                    <div
                      className="w-16 h-16 rounded-lg border-2 border-dashed border-border flex items-center justify-center flex-shrink-0 cursor-pointer hover:border-text-secondary transition-colors"
                      onClick={() => logoInputRef.current?.click()}
                    >
                      <div className="w-8 h-8 rounded-full opacity-40" style={{ backgroundColor: teamColor }} />
                    </div>
                  )}
                  <div className="flex-1">
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      className="w-full py-2 px-3 text-xs font-semibold text-text-primary border border-border rounded-lg hover:border-text-secondary transition-colors text-left"
                    >
                      {currentLogoSrc ? 'Replace Logo' : 'Upload Logo'}
                    </button>
                    <p className="text-xs text-text-muted mt-1">PNG, JPG, WEBP · max 5 MB</p>
                  </div>
                </div>
                <input ref={logoInputRef} type="file" accept={ACCEPTED_TYPES} onChange={handleLogoFileChange} className="hidden" />
              </div>
            </div>

            {formError && <p className="text-error text-sm mb-3">{formError}</p>}

            <div className="space-y-2">
              <button onClick={handleSaveTeam} disabled={saving} className="btn-primary bg-serving text-white font-bold disabled:opacity-50">
                {saving ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    SAVING...
                  </span>
                ) : 'SAVE TEAM'}
              </button>
              <button onClick={() => setTeamForm(null)} disabled={saving} className="btn-secondary text-text-secondary">CANCEL</button>
            </div>
          </div>
        </div>
      )}

      {/* Player Form Modal */}
      {playerForm && (
        <div className="fixed inset-0 bg-black/80 z-40 flex items-end">
          <div className="w-full max-w-md mx-auto bg-surface border-t border-border rounded-t-2xl p-4 pb-safe animate-slide-up">
            <div className="w-12 h-1 bg-border rounded-full mx-auto mb-4" />
            <h2 className="text-lg font-bold text-text-primary text-center mb-1">
              {playerForm.mode === 'add' ? 'ADD PLAYER' : 'EDIT PLAYER'}
            </h2>
            <p className="text-text-muted text-xs text-center mb-4">
              {teams.find(t => t.id === playerForm.teamId)?.name}
            </p>

            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs text-text-muted uppercase mb-1">Player Name</label>
                <input type="text" value={playerName} onChange={e => setPlayerName(e.target.value)} className="input-field" placeholder="e.g. Varsha" maxLength={50} />
              </div>
              <div>
                <label className="block text-xs text-text-muted uppercase mb-1">Player Number (1–99)</label>
                <input type="number" inputMode="numeric" value={playerNumber} onChange={e => setPlayerNumber(e.target.value === '' ? '' : parseInt(e.target.value))} className="input-field" placeholder="e.g. 7" min={1} max={99} />
              </div>
            </div>

            {formError && <p className="text-error text-sm mb-3">{formError}</p>}

            <div className="space-y-2">
              <button onClick={handleSavePlayer} disabled={saving} className="btn-primary bg-serving text-white font-bold disabled:opacity-50">
                {saving ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    SAVING...
                  </span>
                ) : 'SAVE PLAYER'}
              </button>
              <button onClick={() => setPlayerForm(null)} disabled={saving} className="btn-secondary text-text-secondary">CANCEL</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/80 z-40 flex items-end">
          <div className="w-full max-w-md mx-auto bg-surface border-t border-border rounded-t-2xl p-4 pb-safe animate-slide-up">
            <div className="w-12 h-1 bg-border rounded-full mx-auto mb-4" />
            <h2 className="text-lg font-bold text-text-primary text-center mb-2">DELETE {deleteTarget.type.toUpperCase()}?</h2>
            <p className="text-text-secondary text-sm text-center mb-1">
              <span className="font-semibold text-text-primary">{deleteTarget.name}</span>
            </p>
            {deleteTarget.type === 'team' && (
              <p className="text-error/80 text-xs text-center mb-4">All players in this team will also be deleted.</p>
            )}
            {deleteTarget.type === 'player' && <div className="mb-4" />}

            <div className="space-y-2">
              <button onClick={handleDelete} disabled={deleting} className="btn-primary bg-error text-white font-bold disabled:opacity-50">
                {deleting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    DELETING...
                  </span>
                ) : `DELETE ${deleteTarget.type.toUpperCase()}`}
              </button>
              <button onClick={() => setDeleteTarget(null)} disabled={deleting} className="btn-secondary text-text-secondary">CANCEL</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
